import React, { useState, useEffect } from "react";
import styles from "./CodigoFin.module.css";
import img from "../../../resources/img/elcodigo.webp";
import { authCursoLogic, ClaseData } from "../../../../Logic/AuthCurso";
import { c7Api } from "../../../../Logic/c7Api";

interface CodigoFinProps {
  onBack: () => void; // Prop para regresar
  periodo: string; // Periodo en que el usuario se inscribió para el curso
}

// Props del hijo (Agregamos isException)
interface AccordionProps {
  index: number;
  data: ClaseData;
  status: "locked_past" | "active" | "future";
  isException: boolean; 
}

// --- Componente de Acordeón Individual ---
const ClassAccordion: React.FC<AccordionProps> = ({ index, data, status, isException }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Permitimos abrir si es la clase ACTIVA (Semana actual) O si viene en la lista de excepciones
  const handleToggle = () => {
    if (status === "active" || isException) {
      setIsOpen(!isOpen);
    }
  };

  // Si ya pasó pero el usuario es una excepción, NO se bloquea (no se pone gris)
  const isPast = status === "locked_past" && !isException;
  
  return (
    <div className={styles.accordionWrapper}>
      {/* Cabecera */}
      <div 
        className={`${styles.accordionHeader} ${isPast ? styles.locked : ""}`} 
        onClick={handleToggle}
      >
        <div className={styles.leftContent}>
          <div className={styles.thumbnailIcon}>
            <img src={img} alt="thumb" style={isPast ? {filter: 'grayscale(100%)'} : {}} />
          </div>
          <div className={styles.textInfo}>
            <h4>
              {data.title.toUpperCase()} {isException && ""}
            </h4>
          </div>
        </div>

        <div className={styles.rightContent}>
          {/* Si ya pasó la semana y NO es excepción, sale checkeado siempre */}
          <div 
            className={`${styles.statusIcon} ${isPast ? styles.autoChecked : ""}`}
          >
            {isPast && "✔"}
          </div>
          
          {/* Flecha (Se muestra si está activo O si el usuario es una excepción para poder desplegar) */}
          {(!isPast || isException) && (
            <div className={`${styles.arrowIcon} ${isOpen ? styles.rotated : ""}`}>
              ▼
            </div>
          )}
        </div>
      </div>

      {/* Cuerpo Desplegable (Abre si está activo u open por la excepción) */}
      {isOpen && (!isPast || isException) && (
        <div className={styles.accordionBody}>
          <div className={styles.videoContainer}>
            <iframe
              width="100%"
              height="315"
              src={ index === 0 ? `https://www.youtube.com/embed/${data.youtubeid}` : `https://player.vimeo.com/video/${data.youtubeid}?h=0&badge=0&autopause=0&player_id=0&app_id=58479`}
              title="Vimeo video player"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          {/* Botón de descarga */}
          <div className={styles.materialContainer}>
            <a 
              href={c7Api.getBaseUrl() + ( c7Api.getBaseUrl().includes("127.0.0.1") ? data.pdfName : "/Server/public" + data.pdfName)} 
              download={data.title + ".pdf"}        
              target="_blank"           
              rel="noopener noreferrer"       
              className={styles.downloadButton}
            >
              <span role="img" aria-label="pdf">📄</span> Descargar clase complementaria
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Componente Principal ---
const CodigoFin: React.FC<CodigoFinProps> = ({ onBack, periodo }) => {
  
  const [currentWeek, setCurrentWeek] = useState(0);
  const [allClasses, setAllClasses] = useState<ClaseData[]>([]);

  // 🌟 1. LEER EL LOCALSTORAGE PARA OBTENER EL EMAIL
  const localStorageUser = localStorage.getItem("usuarioRegistrado");
  const userEmail = localStorageUser ? JSON.parse(localStorageUser).email : null;

  // 🌟 2. LISTA DE CORREOS QUE TIENEN ACCESO A LAS CLASES BLOQUEADAS
  // Aquí vas agregando los correos de las personas que te lo soliciten
  const listaExcepciones = [
    "nutricion_cenhch@hotmail.com",
  ];

  // 🌟 3. EVALUAR SI EL CORREO ACTUAL ESTÁ EN LA LISTA
  const tieneAccesoTotal = listaExcepciones.includes(userEmail);

  // Cálculo de Fecha al montar el componente
  useEffect(() => {
    // Lista de todas las clases
    (async function() { 
      await authCursoLogic.GetAllClassesCurso().then((data) => {
          setAllClasses(data);
      });
    })();

    const now = new Date(); // Fecha actual

    // --- CONFIGURACIÓN ---
    let START_DATE = new Date("2026-01-12T00:00:00"); // default 

    if(periodo === "Enero 2026")
       START_DATE = new Date("2026-01-12T00:00:00"); 
    else if(periodo === "Abril 2026")
       START_DATE = new Date("2026-04-13T00:00:00"); 
    
    // Si la fecha actual es ANTES del inicio, estamos en la semana 0
    if (now < START_DATE) {
        setCurrentWeek(0); 
        return;
    }

    // Diferencia en milisegundos
    const diffTime = Math.abs(now.getTime() - START_DATE.getTime());
    // Convertir a semanas
    const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
    
    setCurrentWeek(diffWeeks); 
  }, [periodo]);

  // Filtrar: Solo mostramos clases hasta la semana actual
  const visibleClasses = allClasses.slice(0, currentWeek + 1);
  
  // Le damos la vuelta para que la más reciente quede arriba
  const reversedClasses = [...visibleClasses].reverse();

  // Porcentaje basado en el tiempo transcurrido
  const progressPercentage = Math.min(Math.round((currentWeek / allClasses.length) * 100), 100);

  return (
    <div className={styles.coursePageContainer}>
      
      {/* --- BOTÓN FLOTANTE DE REGRESO --- */}
      <button className={styles.floatingBackButton} onClick={onBack}>
         Regresar
      </button>

      {/* Sección Superior */}
      <div className={styles.topSection}>
        <div className={styles.courseSummaryCard}>
          <div className={styles.tapeDeco}></div>

          <div className={styles.cardContent}>
            <div className={styles.infoColumn}>
              <h1>El CÓDIGO DEL FIN</h1>
              <p>
               Este curso dura 1 año. Cada semana se desbloqueará una nueva enseñanza 
               automáticamente. Las clases anteriores se marcarán como completadas.
              </p>
              
              {/* Barra de Progreso */}
              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className={styles.progressLabels}>
                  <span>Inicio</span>
                  <span style={{fontSize: '0.8rem'}}>Semana {currentWeek + 1} de {allClasses.length}</span>
                  <span>Fin</span>
                </div>
              </div>
            </div>

            <div className={styles.imageColumn}>
              <img src={img} alt="Curso" />
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Clases */}
      <div className={styles.classesListContainer}>
        {/* Mensaje al inicio de la lista */}
        <div style={{textAlign: 'center', marginTop: '40px', color: '#666', fontStyle: 'italic'}}>
            Próxima clase disponible en {8 - (new Date().getDay() === 0 ? 7 : new Date().getDay())} días...
        </div>

       {reversedClasses.map((clase, index) => { 
          const originalIndex = visibleClasses.length - 1 - index; 
          
          let status: "locked_past" | "active" | "future" = "future";
          
          if (originalIndex < currentWeek) {
             status = "locked_past"; // Semanas anteriores
          } else if (originalIndex === currentWeek) {
             status = "active";      // Semana actual
          }

          return (
            <ClassAccordion 
              key={originalIndex}
              index={originalIndex} 
              data={clase} 
              status={status}
              isException={tieneAccesoTotal} // 🌟 4. HEREDAMOS SI ES EXCEPCIÓN AL ACORDEÓN
            />
          );
        })}
      </div>
    </div>
  );
};

export default CodigoFin;